import React, { useEffect, useState } from 'react';
import observabilidadeService from '../../services/observabilidadeService';

const CATEGORY_BADGE = {
  Success: { cls: 'bg-success', label: 'Success' },
  Error:   { cls: 'bg-danger',  label: 'Error' },
  Special: { cls: 'bg-warning text-dark', label: 'Special' },
};

const ROW_CLASS = {
  Error:   'table-danger',
  Special: 'table-warning',
  Success: '',
};

function pad(n) {
  return String(n).padStart(2, '0');
}

function formatarDataHora(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function ResumoCabecalho({ id, historico, usinaNome }) {
  const ordenado = [...historico].sort(
    (a, b) => new Date(a.processedAt) - new Date(b.processedAt)
  );
  const primeiro = ordenado[0];
  const ultimo = ordenado[ordenado.length - 1];
  const statusAtual = ultimo.indirectMonitoringProcessmentStatus;

  return (
    <div className="alert alert-info mb-3">
      <div className="row g-2">
        <div className="col-sm-6 col-md-3">
          <small className="text-muted d-block">ID consultado</small>
          <strong>{id}</strong>
        </div>
        <div className="col-sm-6 col-md-3">
          <small className="text-muted d-block">Usina</small>
          <strong>{usinaNome || '—'}</strong>
        </div>
        <div className="col-sm-6 col-md-3">
          <small className="text-muted d-block">Total de registros</small>
          <strong>{historico.length}</strong>
        </div>
        <div className="col-sm-6 col-md-3">
          <small className="text-muted d-block">Status atual</small>
          <strong>{statusAtual?.description ?? '—'}</strong>
          {statusAtual?.category && (
            <span className={`badge ms-1 ${CATEGORY_BADGE[statusAtual.category]?.cls ?? 'bg-secondary'}`}>
              {statusAtual.category}
            </span>
          )}
        </div>
        <div className="col-sm-6 col-md-3">
          <small className="text-muted d-block">Período</small>
          <small>
            {formatarDataHora(primeiro?.processedAt)}
            <br />
            até {formatarDataHora(ultimo?.processedAt)}
          </small>
        </div>
      </div>
    </div>
  );
}

function TabelaHistorico({ historico, usinaNome }) {
  const ordenado = [...historico].sort(
    (a, b) => new Date(a.processedAt) - new Date(b.processedAt)
  );

  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered table-hover mb-0 align-middle">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Data/Hora</th>
            <th>Status ID</th>
            <th>Usina</th>
            <th>Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {ordenado.map((item, idx) => {
            const status = item.indirectMonitoringProcessmentStatus;
            const category = status?.category;
            return (
              <tr key={item.id} className={ROW_CLASS[category] ?? ''}>
                <td>{idx + 1}</td>
                <td className="text-nowrap">{formatarDataHora(item.processedAt)}</td>
                <td>{item.indirectMonitoringProcessmentStatusId}</td>
                <td>{usinaNome || '—'}</td>
                <td>{item.details || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PainelAcompanhamento() {
  const [inputId, setInputId] = useState('');
  const [inputErro, setInputErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [erroApi, setErroApi] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [idConsultado, setIdConsultado] = useState(null);
  const [usinaNome, setUsinaNome] = useState(null);

  const [ultimos5, setUltimos5] = useState(null);
  const [loadingUltimos5, setLoadingUltimos5] = useState(false);
  const [erroUltimos5, setErroUltimos5] = useState(null);
  const [companyMapUltimos5, setCompanyMapUltimos5] = useState(new Map());
  const [usinaFiltroUltimos5, setUsinaFiltroUltimos5] = useState('');

  useEffect(() => {
    async function carregarUltimos5() {
      setLoadingUltimos5(true);
      setErroUltimos5(null);
      try {
        const [dados, todasCompanies] = await Promise.all([
          observabilidadeService.getUltimosMonitoramentos(5),
          observabilidadeService.getAllCompanies(),
        ]);

        const novoMap = new Map();
        for (const company of todasCompanies) {
          novoMap.set(company.id, company.description);
        }
        setCompanyMapUltimos5(novoMap);
        setUltimos5(dados);
      } catch (err) {
        setErroUltimos5(err.message || 'Erro ao carregar.');
      } finally {
        setLoadingUltimos5(false);
      }
    }
    carregarUltimos5();
  }, []);

  function validarId(valor) {
    const n = Number(valor);
    if (!valor.trim() || !Number.isInteger(n) || n <= 0) {
      return 'Informe um número inteiro positivo.';
    }
    return '';
  }

  async function handleBuscar(e) {
    e.preventDefault();
    const erroValidacao = validarId(inputId);
    if (erroValidacao) {
      setInputErro(erroValidacao);
      return;
    }
    setInputErro('');
    setErroApi(null);
    setHistorico(null);
    setUsinaNome(null);
    setLoading(true);

    const id = Number(inputId);
    try {
      const dados = await observabilidadeService.getHistoricoProcessamento(id);
      setIdConsultado(id);
      setHistorico(dados);

      // Buscar nome da usina (não bloqueia exibição do histórico em caso de erro)
      try {
        const monitoring = await observabilidadeService.getMonitoramentoById(id);
        const company = await observabilidadeService.getCompanyById(monitoring.companyId);
        setUsinaNome(company.description);
      } catch {
        setUsinaNome(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setErroApi('Monitoramento Indireto não encontrado.');
      } else {
        setErroApi(err.message || 'Erro ao buscar histórico. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  const ultimos5Filtrados = usinaFiltroUltimos5
    ? (ultimos5 || []).filter(m => String(m.companyId) === usinaFiltroUltimos5)
    : (ultimos5 || []);

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Acompanhamento</h5>
      </div>

      <div className="card-body">
        {/* Legenda do fluxo */}
        <div className="alert alert-secondary py-2 mb-3 small">
          <strong>Fluxo esperado de processamento:</strong>{' '}
          [IndirectMonitoring: IDs 1–6] → [FlydetectorRequest: IDs 7–15 + 24] → [FlydetectorResponse: IDs 16–23]
          <br />
          Status de erro: IDs 1001–1024 &nbsp;|&nbsp; Status final de sucesso: ID 22 &nbsp;|&nbsp; Status especial (fallback): ID 23
        </div>

        {/* Formulário de busca */}
        <form className="row g-2 align-items-start mb-4" onSubmit={handleBuscar}>
          <div className="col-auto">
            <label htmlFor="inputMonitoramentoId" className="col-form-label">
              ID do Monitoramento Indireto
            </label>
          </div>
          <div className="col-auto">
            <input
              id="inputMonitoramentoId"
              type="number"
              min="1"
              step="1"
              className={`form-control${inputErro ? ' is-invalid' : ''}`}
              placeholder="Ex: 12345"
              value={inputId}
              onChange={e => {
                setInputId(e.target.value);
                if (inputErro) setInputErro('');
              }}
            />
            {inputErro && <div className="invalid-feedback">{inputErro}</div>}
          </div>
          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                  Buscando…
                </>
              ) : (
                'Buscar Histórico'
              )}
            </button>
          </div>
        </form>

        {/* Painel Últimos 5 Monitoramentos — só quando campo ID está vazio */}
        {inputId.trim() === '' && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0 text-muted">Últimos 5 Monitoramentos Indiretos</h6>
              {companyMapUltimos5.size > 0 && (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '160px' }}
                  value={usinaFiltroUltimos5}
                  onChange={e => setUsinaFiltroUltimos5(e.target.value)}
                  disabled={loadingUltimos5}
                >
                  <option value="">Todas as Usinas</option>
                  {[...companyMapUltimos5.entries()].map(([id, desc]) => (
                    <option key={id} value={String(id)}>{desc}</option>
                  ))}
                </select>
              )}
            </div>

            {loadingUltimos5 && (
              <div className="d-flex justify-content-center py-3">
                <div className="spinner-border spinner-border-sm text-secondary" role="status">
                  <span className="visually-hidden">Carregando…</span>
                </div>
              </div>
            )}

            {!loadingUltimos5 && erroUltimos5 && (
              <div className="alert alert-warning py-2 small">{erroUltimos5}</div>
            )}

            {!loadingUltimos5 && !erroUltimos5 && ultimos5Filtrados.length === 0 && (
              <div className="text-muted small text-center py-2">
                Nenhum monitoramento recente encontrado.
              </div>
            )}

            {!loadingUltimos5 && !erroUltimos5 && ultimos5Filtrados.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Usina</th>
                      <th>Data Coleta</th>
                      <th>E-mail</th>
                      <th>ID Armadilha</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimos5Filtrados.map(m => (
                      <tr key={m.id}>
                        <td>{m.id}</td>
                        <td>{companyMapUltimos5.get(m.companyId) ?? `ID ${m.companyId}`}</td>
                        <td className="text-nowrap">{formatarDataHora(m.collectDateTime)}</td>
                        <td>{m.userEmail}</td>
                        <td>{m.flyTrapId ?? '—'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary py-0"
                            onClick={() => setInputId(String(m.id))}
                          >
                            Ver Histórico
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Spinner central */}
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando…</span>
            </div>
          </div>
        )}

        {/* Erro de API */}
        {!loading && erroApi && (
          <div className="alert alert-danger">{erroApi}</div>
        )}

        {/* Resultado */}
        {!loading && historico !== null && (
          <>
            {historico.length === 0 ? (
              <div className="alert alert-warning">
                Nenhum histórico de processamento encontrado para este ID.
              </div>
            ) : (
              <>
                <ResumoCabecalho id={idConsultado} historico={historico} usinaNome={usinaNome} />
                <TabelaHistorico historico={historico} usinaNome={usinaNome} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PainelAcompanhamento;
